/* eslint-disable prefer-destructuring */
/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
import moment from 'moment';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  updateParentRows,
  updateDataForPercentageRow,
  updateDataForSelectedBoolean,
  updateDataForPPGRows
} from 'src/components/collapsible-table/table-data-prep';
import { IScreen } from 'src/components/user/user/types/screens-model';
import {
  fetchSimulationTableInputs,
  getListOfScenarioNames,
  saveScenarioData,
  updateScenarioData,
  fetchPricingSimulationData,
  saveSimulationData,
  simulatePricingSimulationData,
  resetSimulationData,
  fetchTopLineInputsData
} from '../api';
import KPISelection from '../types/kpi-selection.enum';
import {
  IDataRow,
  ISimTableColumnMetadata,
  TableMetadata
} from '../types/simulation-view-table.model';
import {
  filterColumnsForToggleView,
  getAccessorFields,
  getCompleteRowObjectsForUpdatedRows,
  updateLinkedFieldPercOrAdditionalChange
} from '../utils/column-utils';
import { calculatePercentageChange } from '../utils/number-utils';
import {
  Header,
  ITableData,
  MODIFIED_ROW_INDICATOR_KEY,
  TypeOfTableData
} from '../types/generic-table.model';
import { resetTopLineInputData } from '../components/topline-inputs-modal/topline-input.util';
import { formatTopLineInput } from '../utils/topline-utils';
import { IMPLEMENTATION_PERIOD_1_KEY, IMPLEMENTATION_PERIOD_2_KEY } from 'src/constants';
import { updateDropdownSelection } from '../../common/features/table/store/table-slice';
import { calculateGlobalValues } from '../utils/price-action-utils';

/* 
  Reset the simulation history/data
*/
const resetPricingSimulationData = createAsyncThunk(
  'getPricingSimulationData',
  async ({
    sourceId,
    moduleId,
    scenarioId
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  },
    thunkAPI
  ) => {
    const response = await resetSimulationData(sourceId, moduleId, scenarioId);
    const { globalPA1Value, globalPA2Value } = calculateGlobalValues(
      response.row,
      'pricing_implement_after_cal_1',
      'pricing_implement_after_cal_2'
    );
    // Dispatch actions to update Redux state
    thunkAPI.dispatch(
      updateDropdownSelection({
        columnKey: 'pricing_implement_after_cal_1',
        value: globalPA1Value,
      })
    );
    thunkAPI.dispatch(
      updateDropdownSelection({
        columnKey: 'pricing_implement_after_cal_2',
        value: globalPA2Value,
      })
    );
    let kpiSelectionData = [];
    let toggle1 = { name: '', value: '' };
    let toggle2 = { name: '', value: '' };
    Object.keys(response.toggle.data).forEach((toggleVal, index) => {
      kpiSelectionData.push({
        name: response.toggle.data[toggleVal].alias,
        value: response.toggle.data[toggleVal].name,
        pkId: response.toggle.data[toggleVal].pk_id,
        order: response.toggle.data[toggleVal].order
      });
    });
    const priceThresholdData = extractDataForPriceThreshold(response);
    const collapsableColumns = getAllCollapsableColumns(response.header);
    kpiSelectionData = kpiSelectionData.sort((a, b) => a.order - b.order);
    kpiSelectionData.forEach((selectionData, index: number) => {
      if (index === 0) {
        toggle1 = {
          name: selectionData.name,
          value: selectionData.value
        };
      } else {
        toggle2 = {
          name: selectionData.name,
          value: selectionData.value
        };
      }
    });
    const ownKPIResultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      toggle1.value
    );
    const ownKPIInputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      toggle1.value
    );
    const retailerKPIInputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      toggle2.value
    );
    const retailerKPIResultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      toggle2.value
    );
    return {
      response,
      ownKPIResultCols,
      ownKPIInputCols,
      retailerKPIInputCols,
      retailerKPIResultCols,
      topline_filters: response.topline_filters,
      priceThresholdData,
      collapsableColumns
    };
  }
);

/* 
  Reset the simulation history/data
*/
const savePricingSimulationData = createAsyncThunk(
  'savePricingSimulationData',
  async ({
    sourceId,
    moduleId,
    scenarioId
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  }) => {
    const response = await saveSimulationData(sourceId, moduleId, scenarioId);
    return response;
  }
);

/* To fetch the data for topline inputs */
const getTopLineInputsData = createAsyncThunk(
  'getTopLineInputsData',
  async ({
    sourceId,
    moduleId,
    scenarioId,
    filterInput,
    toplineFields
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    filterInput: { filter_inputs: any };
    toplineFields: any;
  }) => {
    const response = await fetchTopLineInputsData(
      sourceId,
      moduleId,
      scenarioId,
      filterInput.filter_inputs,
      toplineFields
    );
    return response;
  }
);

function extractDataForPriceThreshold(response: any) {
  const is_price_threshold_eligible = {};
  let child_field_ppg = '';

  function traverse(nodes: any) {
    nodes.forEach((node: any) => {
      const parentField = node?.parent?.parent_field;
      const isEligible = node?.parent?.is_price_threshold_eligible || false;
      const childField = node?.parent?.child_field;

      // Only add to is_price_threshold_eligible if parent_field is not empty
      if (parentField) {
        is_price_threshold_eligible[parentField] = isEligible;
      }
      if (childField === 'ppg') {
        child_field_ppg = parentField;
      }

      // Recursively check for children
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    });
  }

  traverse(response.header);

  return { is_price_threshold_eligible, child_field_ppg };
}
function getAllCollapsableColumns(headerData: any[]): Record<string, boolean> {
  return headerData.reduce((acc, item) => {
    if (item.parent.is_visible && item.parent.alias !== 'legendflag' && Array.isArray(item.children) && item.children.length > 1) {
      acc[item.parent.alias] = false;
    }
    return acc;
  }, {} as Record<string, boolean>);
}

const getPricingSimulationData = createAsyncThunk(
  'getPricingSimulationData',
  async ({
    dataInput,
    sourceId,
    moduleId,
    scenarioId
  }: {
    dataInput: any;
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  },
    thunkAPI
  ) => {
    const isSimulation =
      (dataInput.row && dataInput.row.data && dataInput.row.data.length) ||
      (dataInput.row && dataInput.row.inputs && dataInput.row.inputs.length);
    const response = isSimulation
      ? await simulatePricingSimulationData(
        dataInput,
        sourceId,
        moduleId,
        scenarioId
      )
      : await fetchPricingSimulationData(
        dataInput,
        sourceId,
        moduleId,
        scenarioId
      );
    // setting global value for PA1 and PA2 dropdowns
    // Extract values for PA1 and PA2
    const { globalPA1Value, globalPA2Value } = calculateGlobalValues(
      response.row,
      'pricing_implement_after_cal_1',
      'pricing_implement_after_cal_2'
    );
    // Dispatch actions to update Redux state
    thunkAPI.dispatch(
      updateDropdownSelection({
        columnKey: 'pricing_implement_after_cal_1',
        value: globalPA1Value,
      })
    );
    thunkAPI.dispatch(
      updateDropdownSelection({
        columnKey: 'pricing_implement_after_cal_2',
        value: globalPA2Value,
      })
    );
    let kpiSelectionData = [];
    let toggle1 = { name: '', value: '' };
    let toggle2 = { name: '', value: '' };
    Object.keys(response.toggle.data).forEach((toggleVal, index) => {
      kpiSelectionData.push({
        name: response.toggle.data[toggleVal].alias,
        value: response.toggle.data[toggleVal].name,
        pkId: response.toggle.data[toggleVal].pk_id
      });
    });
    const priceThresholdData = extractDataForPriceThreshold(response);
    const collapsableColumns = getAllCollapsableColumns(response.header);
    kpiSelectionData = kpiSelectionData.sort((a, b) => a.order - b.order);
    kpiSelectionData.forEach((selectionData, index: number) => {
      if (index === 0) {
        toggle1 = {
          name: selectionData.name,
          value: selectionData.value
        };
      } else {
        toggle2 = {
          name: selectionData.name,
          value: selectionData.value
        };
      }
    });

    const ownKPIResultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      toggle1.value
    );

    const ownKPIInputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      toggle1.value
    );
    const retailerKPIInputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      toggle2.value
    );
    const retailerKPIResultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      toggle2.value
    );

    return {
      response,
      ownKPIResultCols,
      ownKPIInputCols,
      retailerKPIInputCols,
      retailerKPIResultCols,
      isSimulation,
      topline_filters: response.topline_filters,
      priceThresholdData,
      collapsableColumns
    };
  }
);

const getNewInputsForAppliedFilters = createAsyncThunk(
  'getNewInputsForAppliedFilters',
  async (
    { filters }: { filters: { field_name: string; field_values: any }[] },
    thunkAPI
  ) => {
    const response = await fetchSimulationTableInputs({
      source_id: (thunkAPI.getState() as any).user.source,
      screen_id:
        (thunkAPI.getState() as any).user.screenInfo.find(
          (screen: IScreen) => screen.name === 'Pricing Simulator'
        )?.id || 41,
      simulation_id: (thunkAPI.getState() as any).pricingSimulator
        .currentSimulationId,
      filter_inputs: filters
    });
    return response;
  }
);

const saveScenarioDetails = createAsyncThunk(
  'saveScenarioDetails',
  async (scenarioData: any, thunkAPI) => {
    const response = await saveScenarioData({
      source_id: (thunkAPI.getState() as any).user.source,
      simulation_id: (thunkAPI.getState() as any).pricingSimulator
        .currentSimulationId,
      name: scenarioData.name,
      description: scenarioData.description,
      price_action_at: scenarioData.priceActionDate
        ? scenarioData.priceActionDate
        : moment().format(),
      is_active: true
    });
    return response.data;
  }
);

const updateScenarioDetails = createAsyncThunk(
  'updateScenarioDetails',
  async (scenarioData: any, thunkAPI) => {
    const response = await updateScenarioData({
      source_id: (thunkAPI.getState() as any).user.source,
      simulation_id:
        scenarioData.simulationId ||
        (thunkAPI.getState() as any).pricingSimulator.currentSimulationId,
      name: scenarioData.name,
      description: scenarioData.description,
      price_action_at: moment().format(),
      is_active: true
    });
    return response.data;
  }
);

// TODO: Parameterize the hardcoded argument
const getListOfSavedScenarioNames = createAsyncThunk(
  'getListOfSavedScenarioNames',
  async (sourceId: number, thunkAPI) => {
    const response = await getListOfScenarioNames(
      (thunkAPI.getState() as any).user.source
    );
    return response.data;
  }
);

// interface for topline input dropdown fields
export interface IFieldObj {
  id: number;
  source: string;
  order: number;
  name: string;
  alias: string;
}

interface IListPriceSimulationState {
  fetchingLPData: boolean;
  savingScenarioData: boolean;
  fetchingSimTableMetadata: boolean;
  fetchingPricingSimulationData: boolean;
  enableSimulation: boolean;
  listPriceData: IDataRow[];
  isSimulationSaved: boolean;
  totalRow: IDataRow;
  isResettingTopLineInput: boolean;
  isSimulating: boolean;
  listOfScenariosName: any[];
  isSKUEditedClick: boolean;
  simulationTableMetadata: {
    listPrice: ISimTableColumnMetadata[];
    retailPrice: ISimTableColumnMetadata[];
    retailPriceSimulationResult: ISimTableColumnMetadata[];
    listPriceSimulationResult: ISimTableColumnMetadata[];
    displayColumns: ISimTableColumnMetadata[];
  };
  showEditedRowsOnly: boolean;
  kpiSelection: string;
  simResultsViewSetToAbsolute: boolean;
  currentSimulationId: number;
  // pricingSimulationData?: ITableData;
  pricingSimulationData?: any;
  pricingSimulationTableData?: TableMetadata;
  isScenarioSavedSuccessfully: boolean;
  isScenarioSaving: boolean;
  /* variable for top line input data */
  fetchingTopLineTableData: boolean;
  topLineInputTableData: any;
  topLineFieldsPriorityData: IFieldObj[];
  topLineTableInputCols: Header[];
  toggle1: { [key: string]: string };
  toggle2: { [key: string]: string };
  getToggleLabelName: string;
  toggleData: { [key: string]: string }[];
  enableTopLineApplyButton: boolean;
  selectedScenarioIDarray: any;
  selectedScenarioArrayWithOrder: any;
  extRowColumnNumber: any;
  columnsMapping: any;
  kpi2ColumnMapping: string[];
  collapsableColumns: Record<string, boolean>;
  visibleColumns: Record<string, boolean>;
  activeHeader: string;
  topLineInputTableGlobalValue: { [key: string]: number | string };
  lastSimulatedSnapshot: any | null;
}

const initialState: IListPriceSimulationState = {
  fetchingLPData: false,
  savingScenarioData: false,
  listPriceData: [],
  listOfScenariosName: [],
  isSimulationSaved: false,
  isSimulating: false,
  isScenarioSavedSuccessfully: false,
  isSKUEditedClick: false,
  isScenarioSaving: false,
  isResettingTopLineInput: false,
  toggleData: [],
  totalRow: {},
  enableSimulation: false,
  fetchingSimTableMetadata: false,
  simulationTableMetadata: {
    listPrice: [],
    retailPrice: [],
    retailPriceSimulationResult: [],
    listPriceSimulationResult: [],
    displayColumns: []
  },
  showEditedRowsOnly: false,
  simResultsViewSetToAbsolute: true,
  enableTopLineApplyButton: false,
  currentSimulationId: null,
  toggle1: {},
  toggle2: {},
  kpiSelection: KPISelection.OWN_KPI,
  fetchingTopLineTableData: false,
  topLineInputTableData: {
    legend: { metadata: [] },
    ownKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    retailerKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    metadata: [],
    show_edited: {
      enabled: false,
      label: ''
    },
    row: [],
    toggle: {
      metadata: [],
      data: {}
    },
    output_toggle: {
      metadata: [],
      data: {}
    },
    footer: {
      metadata: {
        enabled: false,
        label: ''
      },
      row: []
    },
    header: []
  },
  topLineFieldsPriorityData: [],
  topLineTableInputCols: [],
  pricingSimulationData: {
    legend: { metadata: [] },
    ownKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    retailerKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    metadata: [],
    show_edited: {
      enabled: false,
      label: ''
    },
    row: [],
    toggle: {
      metadata: [],
      data: {}
    },
    output_toggle: {
      metadata: [],
      data: {}
    },
    footer: {
      metadata: {
        enabled: false,
        label: ''
      },
      row: []
    },
    header: []
  },
  getToggleLabelName: '',
  pricingSimulationTableData: {
    data: [],
    displayColumnData: [],
    inputColumnData: [],
    absOutputColumnData: [],
    percOutputColumnData: []
  },
  fetchingPricingSimulationData: false,
  selectedScenarioIDarray: [],
  selectedScenarioArrayWithOrder: [],
  extRowColumnNumber: {},
  columnsMapping: {},
  kpi2ColumnMapping: [],
  collapsableColumns: {},
  visibleColumns: {},
  activeHeader: '',
  topLineInputTableGlobalValue: {},
  lastSimulatedSnapshot: null
};

const pricingSimulatorSlice = createSlice({
  name: 'pricing-simulator',
  initialState,
  reducers: {
    toggleKPISelection(state, action: PayloadAction<KPISelection>) {
      if (action.payload) {
        state.kpiSelection = action.payload;
      }
    },
    setAllCollapsableColumns(state, action: PayloadAction<Record<string, boolean>>) {
      if (action.payload) {
        state.collapsableColumns = action.payload;
        state.visibleColumns = action.payload;
      }
    },
    toggleVisibleColumns(state, action: PayloadAction<string>) {
      if (action.payload) {
        const value = action.payload;
        const isActive = state.visibleColumns[value];

        const newState = { ...state.collapsableColumns };

        if (!isActive) {
          newState[value] = true;
          state.activeHeader = value;
        } else {
          state.activeHeader = "";
        }
        state.visibleColumns = newState;
      }
    },
    resetVisibleColumns(state) {
      const newState = { ...state.collapsableColumns };
      state.visibleColumns = newState;
      state.activeHeader = "";
    },
    handleNewValueChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
        dataRow: IDataRow;
        changedRows: any;
      }>,
      uniqueId = 'ppg_id'
    ) {
      const { dataRow, changedRows, columnMetadata } = action.payload;
      const rowStateData = [
        state.pricingSimulationData.row[dataRow.index]
          ? state.pricingSimulationData.row[dataRow.index]
          : state.pricingSimulationData.row[dataRow.parent_index]
      ];
      if (changedRows.length) {
        updateParentRows(
          state.pricingSimulationData.row,
          changedRows,
          0,
          dataRow,
          columnMetadata,
          MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_NOT_DEFAULT
        );
      } else {
        state.pricingSimulationData.row[dataRow.index].parent.data[
          MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_EDITED
        ] = true;
      }

      updateDataForPercentageRow(
        state.pricingSimulationData,
        rowStateData,
        uniqueId,
        action
      );
    },
    handleTopLineInputValue(
      state,
      action: PayloadAction<{
        newValue: number | string;
        columnName: string;
        filteredRows: string[];
      }>
    ) {
      const { columnName, newValue, filteredRows } = action.payload;
      const rowsData = state.topLineInputTableData.row.slice();
      rowsData.forEach((element) => {
        if (
          _.isEmpty(filteredRows) ||
          (filteredRows.length &&
            filteredRows.includes(element.parent.data.index))
        ) {
          element.parent.data[columnName] = newValue;
        }
      });
      state.topLineInputTableData.row = rowsData;
      if (columnName === IMPLEMENTATION_PERIOD_1_KEY || columnName === IMPLEMENTATION_PERIOD_2_KEY) state.topLineInputTableGlobalValue[columnName] = newValue;
    },
    handleTopLineRowInputValue(
      state,
      action: PayloadAction<{
        newValue: number;
        columnName: string;
        rowData: any;
        field1?: any;
        field2?: any;
      }>
    ) {
      const { columnName, newValue, rowData, field1, field2 } = action.payload;
      const index = state.topLineInputTableData.row.findIndex((element) => {
        let currIndex;
        if (field2) {
          currIndex =
            `${element.parent.data[`${field1}_id`]}_${element.parent.data[`${field2}_id`]
            }` === `${rowData[`${field1}_id`]}_${rowData[`${field2}_id`]}`;
        } else {
          currIndex =
            `${element.parent.data[`${field1}_id`]}` ===
            `${rowData[`${field1}_id`]}`;
        }

        return currIndex;
      });
      state.topLineInputTableData.row[index].parent.data[columnName] = newValue;
    },
    handleValueChangeForDropdown(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
        dataRow: IDataRow;
        isRowCanExpand: boolean;
      }>
    ) {
      const { columnMetadata, newValue, dataRow } = action.payload;
      dataRow[columnMetadata.parent.parent_field] = newValue;
      const updatedColumnData = updateDataForSelectedBoolean(
        state.pricingSimulationData,
        action
      );
      state.pricingSimulationData.row = updatedColumnData;
    },
    updateResetValueForTopLine(state) {
      state.isResettingTopLineInput = false;
      state.enableTopLineApplyButton = false;
    },
    updateApplyValueForTopLine(state) {
      state.isResettingTopLineInput = false;
      state.enableTopLineApplyButton = true;
    },
    handleDialogCloseEvent(state) {
      state.isScenarioSavedSuccessfully = false;
    },
    handleSimulationFlagValue(state) {
      state.isSimulating = true;
    },
    handleAllNewInputChange(
      state,
      action: PayloadAction<{
        columnMetadata: any;
        newValue: number;
      }>
    ) {
      const {
        [KPISelection.RETAILER_KPI]: marketField,
        [KPISelection.OWN_KPI]: ppgField
      } = getAccessorFields(state.simulationTableMetadata.displayColumns);
      const { columnMetadata, newValue } = action.payload;
      state.listPriceData
        .filter((rowToUpdate) =>
          state.kpiSelection === KPISelection.OWN_KPI
            ? true
            : rowToUpdate[marketField] !== ''
        )
        .forEach((rowToUpdate) => {
          const currentValue = rowToUpdate[columnMetadata.field_name] as number;
          rowToUpdate[columnMetadata.new_value_field_name] = newValue;
          const percentageChange = calculatePercentageChange(
            newValue,
            currentValue
          );
          if (columnMetadata.is_perc_active) {
            rowToUpdate[columnMetadata.perc_change_value_field_name] =
              percentageChange;
          } else if (columnMetadata.is_additional_active) {
            rowToUpdate[columnMetadata.additional_value_field_name] =
              percentageChange;
          }
          const linkColumnMetadata = [
            ...state.simulationTableMetadata.listPrice,
            ...state.simulationTableMetadata.retailPrice
          ].find(
            (column) =>
              column.field_name === columnMetadata.linked_to_field_name
          );
          if (linkColumnMetadata) {
            // Mutating other properties of the data row
            updateLinkedFieldPercOrAdditionalChange(
              rowToUpdate,
              linkColumnMetadata,
              percentageChange
            );
          }
        });
    },
    toggleShowEditedPPGs(state) {
      state.showEditedRowsOnly = !state.showEditedRowsOnly;
    },
    handleValueOfSavedScenario(state) {
      state.isScenarioSavedSuccessfully = false;
    },
    resetSimulation(state) {
      state.currentSimulationId = undefined;
      state.enableSimulation = false;
    },
    updateTableRowsForBrand(
      state,
      action: PayloadAction<{ updatedRows: any }>
    ) {
      if (action.payload.updatedRows.length) {
        const updatedColumnData = updateDataForPPGRows(
          state.pricingSimulationData,
          action.payload.updatedRows
        );
        state.pricingSimulationData.row = updatedColumnData;
        state.enableTopLineApplyButton = false;
      }
    },
    resetTopLineInputs(state) {
      const updatedData = resetTopLineInputData(
        state.topLineInputTableData.row,
        state.topLineInputTableData.header
      );
      state.isResettingTopLineInput = true;
      state.topLineInputTableData.row = updatedData;
      state.enableTopLineApplyButton = false;
      state.topLineInputTableGlobalValue = {};
    },
    addSelectedScenario(state, action: PayloadAction<{ selectedItemID: any }>) {
      state.selectedScenarioIDarray = [
        ...state.selectedScenarioIDarray,
        action.payload.selectedItemID
      ];
      state.selectedScenarioArrayWithOrder = state.selectedScenarioIDarray.map(
        (item, index) => ({
          scenario_id: item,
          order: index + 1
        })
      );
    },
    removeUnselectedScenario(
      state,
      action: PayloadAction<{ unSelectedItemID: any }>
    ) {
      state.selectedScenarioIDarray = [
        ...state.selectedScenarioIDarray.filter(
          (el) => el != action.payload.unSelectedItemID
        )
      ];
      state.selectedScenarioArrayWithOrder = state.selectedScenarioIDarray.map(
        (item, index) => ({
          scenario_id: item,
          order: index + 1
        })
      );
    },
    resetSelectedScenario(state) {
      state.selectedScenarioIDarray = [];
    },
    setExternalColumnsNumber(state, action) {
      state.extRowColumnNumber = {
        ...state.extRowColumnNumber,
        ...action.payload
      };
    },
    setColumnsMapping(state, action) {
      state.columnsMapping = {
        ...state.columnsMapping,
        ...action.payload
      };
    },
    setKPI2ColumnsMapping(state, action) {
      state.kpi2ColumnMapping = action.payload;
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getPricingSimulationData.pending, (state) => {
        state.fetchingPricingSimulationData = true;
      })
      .addCase(getPricingSimulationData.fulfilled, (state, action) => {
        state.fetchingPricingSimulationData = false;
        // cloning topline filters data
        const toplineFiltersClone = JSON.parse(
          JSON.stringify(action.payload.topline_filters)
        );
        // getting priority data for field dropdown
        state.topLineFieldsPriorityData = toplineFiltersClone.sort((a, b) =>
          a.order > b.order ? 1 : -1
        );
        state.pricingSimulationData = {
          ...state.pricingSimulationData,
          ...action.payload.response
        };
        let kpiSelectionData = [];
        Object.keys(action.payload.response.toggle.data).forEach(
          (toggleVal, index) => {
            kpiSelectionData.push({
              name: action.payload.response.toggle.data[toggleVal].alias,
              value: action.payload.response.toggle.data[toggleVal].name,
              pkId: action.payload.response.toggle.data[toggleVal].pk_id
            });
          }
        );
        kpiSelectionData = kpiSelectionData.sort((a, b) => a.order - b.order);
        kpiSelectionData.forEach((selectionData, index: number) => {
          if (index === 0) {
            state.toggle1 = {
              name: selectionData.name,
              value: selectionData.value
            };
          } else {
            state.toggle2 = {
              name: selectionData.name,
              value: selectionData.value
            };
          }
        });
        if (action.payload.isSimulation) {
          state.kpiSelection = state.kpiSelection
            ? state.kpiSelection
            : state.toggle1.name;
        } else {
          state.kpiSelection = state.toggle1.name;
        }
        state.toggleData = kpiSelectionData;
        if (action.payload.isSimulation) {
          state.isSimulating = false;
          state.enableSimulation = true;
        }
        state.lastSimulatedSnapshot = JSON.parse(
          JSON.stringify(action.payload.response)
        );
      })
      .addCase(getPricingSimulationData.rejected, (state) => {
        state.fetchingPricingSimulationData = false;
        state.isSimulating = false;
      })
      .addCase(getTopLineInputsData.pending, (state) => {
        state.fetchingTopLineTableData = true;
        state.topLineInputTableGlobalValue = {};
      })
      .addCase(getTopLineInputsData.fulfilled, (state, action) => {
        state.fetchingTopLineTableData = false;
        state.topLineInputTableGlobalValue = {};
        state.topLineInputTableData = formatTopLineInput(action.payload);
        // state.topLineInputTableData = {
        //   ...action.payload
        // };

        state.topLineTableInputCols = filterColumnsForToggleView(
          [TypeOfTableData.DISPLAY, TypeOfTableData.INPUT],
          action.payload.header,
          ''
        );
      })
      .addCase(getTopLineInputsData.rejected, (state) => {
        state.fetchingTopLineTableData = false;
      })

      .addCase(getNewInputsForAppliedFilters.rejected, (state) => {
        state.fetchingLPData = false;
      })
      .addCase(getNewInputsForAppliedFilters.pending, (state) => {
        state.fetchingLPData = true;
      })
      .addCase(getNewInputsForAppliedFilters.fulfilled, (state, action) => {
        state.fetchingLPData = false;
        // state.listPriceData = action.payload.data.OWN.slice();
        state.totalRow = { ...action.payload.data.TOTAL[0] };
        const updatedRows = getCompleteRowObjectsForUpdatedRows(
          state.listPriceData,
          [
            ...state.simulationTableMetadata.listPrice,
            ...state.simulationTableMetadata.retailPrice
          ],
          state.simulationTableMetadata.displayColumns
        );
        const {
          [KPISelection.OWN_KPI]: ppgField,
          [KPISelection.RETAILER_KPI]: marketField
        } = getAccessorFields(state.simulationTableMetadata.displayColumns);
        state.listPriceData = !updatedRows.length
          ? action.payload.data.OWN.slice()
          : action.payload.data.OWN.slice().map((field) => {
            const updatedRow = updatedRows.find(
              (row) =>
                row[ppgField] === field[ppgField] &&
                row[marketField] === field[marketField]
            );
            if (updatedRow) {
              return { ...field, ...updatedRow };
            }
            return field;
          });
        state.totalRow = { ...action.payload.data.TOTAL[0] };
        state.showEditedRowsOnly = false;
        state.currentSimulationId = action.payload.simulation_id;
      })
      .addCase(saveScenarioDetails.pending, (state) => {
        state.savingScenarioData = true;
      })
      .addCase(saveScenarioDetails.fulfilled, (state, action) => {
        state.savingScenarioData = false;
      })
      .addCase(saveScenarioDetails.rejected, (state) => {
        state.savingScenarioData = false;
      })
      .addCase(savePricingSimulationData.pending, (state) => {
        state.isSimulationSaved = true;
        state.savingScenarioData = false;
        state.isScenarioSaving = true;
        state.isScenarioSavedSuccessfully = false;
      })
      .addCase(savePricingSimulationData.fulfilled, (state) => {
        state.isSimulationSaved = false;
        state.savingScenarioData = true;
        state.isScenarioSaving = false;
        state.isScenarioSavedSuccessfully = true;
      })
      .addCase(savePricingSimulationData.rejected, (state) => {
        state.isSimulationSaved = false;
        state.isScenarioSaving = false;
      })
      .addCase(getListOfSavedScenarioNames.fulfilled, (state, action) => {
        state.listOfScenariosName = action.payload.scenario_names;
      })
      .addCase(updateScenarioDetails.pending, (state) => {
        state.savingScenarioData = true;
      })
      .addCase(updateScenarioDetails.fulfilled, (state) => {
        state.savingScenarioData = false;
      })
      .addCase(updateScenarioDetails.rejected, (state) => {
        state.savingScenarioData = false;
      })
});

export const {
  toggleKPISelection,
  handleNewValueChange,
  toggleShowEditedPPGs,
  handleAllNewInputChange,
  handleValueChangeForDropdown,
  handleSimulationFlagValue,
  handleDialogCloseEvent,
  handleValueOfSavedScenario,
  resetSimulation,
  handleTopLineInputValue,
  updateTableRowsForBrand,
  resetTopLineInputs,
  updateResetValueForTopLine,
  updateApplyValueForTopLine,
  handleTopLineRowInputValue,
  addSelectedScenario,
  removeUnselectedScenario,
  resetSelectedScenario,
  setExternalColumnsNumber,
  setColumnsMapping,
  setKPI2ColumnsMapping,
  toggleVisibleColumns,
  resetVisibleColumns,
  setAllCollapsableColumns
} = pricingSimulatorSlice.actions;

export {
  getNewInputsForAppliedFilters,
  getPricingSimulationData,
  saveScenarioDetails,
  resetPricingSimulationData,
  getTopLineInputsData,
  savePricingSimulationData,
  getListOfSavedScenarioNames,
  updateScenarioDetails
};

export default pricingSimulatorSlice;
